import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createTask } from "@/data/requests/task-requests";
import { Plus } from "lucide-react";

export function CreateTaskDialog({ children, defaultDate }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      title: "",
      description: "",
      isUrgent: false,
      isImportant: false,
      dueDate: defaultDate ? defaultDate.toISOString().slice(0, 16) : "",
      xpReward: 10
    }
  });

  const createMutation = useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast({
        title: t("common.success"),
        description: t("tasks.create.successMessage", { defaultValue: "Your task has been successfully created." }),
      });
      setOpen(false);
      reset();
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description: t("tasks.create.errorMessage", { defaultValue: "Failed to create task. Please try again." }),
        variant: "destructive",
      });
    }
  });

  const onSubmit = (data) => {
    // Client-side validation: dueDate cannot be in the past
    if (data.dueDate) {
      const now = new Date();
      const dueDate = new Date(data.dueDate);
      
      if (dueDate < now) {
        toast({
          title: t("errors.invalidDate", { defaultValue: "Invalid date" }),
          description: t("tasks.create.pastDateError", { defaultValue: "Due date cannot be in the past." }),
          variant: "destructive",
        });
        return;
      }
    }
    createMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button>
            <Plus className="mr-2 h-4 w-4" /> {t("tasks.create.button")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("tasks.create.title")}</DialogTitle>
          <DialogDescription>
            {t("tasks.create.description", { defaultValue: "Add a new task to your list. Click save when you're done." })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">{t("tasks.create.titleLabel", { defaultValue: "Title" })}</Label>
            <Input 
              id="title" 
              placeholder={t("tasks.create.titlePlaceholder")} 
              {...register("title", { required: true })} 
            />
            {errors.title && <span className="text-xs text-red-500">{t("errors.required")}</span>}
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="description">{t("tasks.create.descriptionLabel", { defaultValue: "Description" })}</Label>
            <Textarea 
              id="description" 
              placeholder={t("tasks.create.descriptionPlaceholder")} 
              {...register("description")} 
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="dueDate">{t("tasks.create.dueDate")}</Label>
            <Input 
              id="dueDate" 
              type="datetime-local" 
              min={new Date().toISOString().slice(0, 16)}
              {...register("dueDate")} 
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Switch 
                id="isUrgent" 
                onCheckedChange={(checked) => setValue("isUrgent", checked)} 
              />
              <Label htmlFor="isUrgent">{t("tasks.create.urgent")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch 
                id="isImportant" 
                onCheckedChange={(checked) => setValue("isImportant", checked)} 
              />
              <Label htmlFor="isImportant">{t("tasks.create.important")}</Label>
            </div>
          </div>
        </form>
        <DialogFooter>
          <Button type="submit" onClick={handleSubmit(onSubmit)} disabled={createMutation.isPending}>
            {createMutation.isPending ? t("common.saving") : t("tasks.create.button")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
